import { useState, useMemo } from 'react';
import { Product, CartItem, CartTotals } from '../types/pos.types';
import { TaxCalculator } from '@/config/tax';

export const useCart = () => {
  const [items, setItems] = useState<CartItem[]>([]);

  const addToCart = (product: Product) => {
    setItems(prevItems => {
      // Generar ID único que incluya presentación si existe
      const cartItemId = product.presentationId
        ? `${product.id}-${product.presentationId}`
        : product.id;

      const existingItem = prevItems.find(item => item.id === cartItemId);

      if (existingItem) {
        return prevItems.map(item =>
          item.id === cartItemId
            ? {
                ...item,
                quantity: item.quantity + 1,
                subtotal: (item.quantity + 1) * product.price
              }
            : item
        );
      }

      const newItem: CartItem = {
        id: cartItemId,
        product,
        quantity: 1,
        subtotal: product.price
      };

      return [...prevItems, newItem];
    });
  };

  const removeFromCart = (productId: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setItems(prevItems =>
      prevItems.map(item =>
        item.id === productId
          ? {
              ...item,
              quantity,
              subtotal: quantity * item.product.price
            }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const totals: CartTotals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
    const breakdown = TaxCalculator.calculateBreakdown(subtotal);

    return {
      subtotal: breakdown.subtotal,
      igv: breakdown.igv,
      otherCharges: breakdown.otherCharges,
      total: breakdown.total
    };
  }, [items]);

  return {
    items,
    totals,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart
  };
};